import React from 'react'
import { useAsyncCallback } from 'react-async-hook'
import { FormProvider, useForm } from 'react-hook-form'
import Skeleton from 'react-loading-skeleton'

import { bemJoin } from 'bem-join'

import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import { CourtCasePaymentEventType, SortDirection } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { useHandleFileUpload } from '@app/lib/hooks/useHandleFileUpload'
import { uploadFiles } from '@app/modules/CourtCases/Components/ChatChronic/helpers/uploadFiles'
import { useCourtCaseContext } from '@app/modules/CourtCases/Components/CourtCaseContext/CourtCaseContext'
import { InvoicingBasicSection } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingBasicSection/InvoicingBasicSection'
import { InvoicingDefaultValuesProvider } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingDefaultValuesContext/InvoicingDefaultValuesContext'
import { InvoicingFeeTablesProvider } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingFeeTablesContext/InvoicingFeeTablesContext'
import { InvoicingFinalizeSection } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingFinalizeSection/InvoicingFinalizeSection'
import { InvoicingInstancesSection } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingInstancesSection/InvoicingInstancesSection'
import { InvoicingOwnCostSection } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingOwnCostSection/InvoicingOwnCostSection'
import { InvoicingTotalCostSection } from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingTotalCostSection/InvoicingTotalCostSection'
import { refetchCourtCasePaymentEventsQuery } from '@app/queries/CourtCasePaymentEvents/CourtCasePaymentEvents.queries'
import { useUpsertCourtCasePaymentEventMutation } from '@app/queries/UpsertCourtCasePaymentEvent/UpsertCourtCasePaymentEvent.queries'

import './styles.scss'

export const CourtCaseInvoicingForm = () => {
  const b = bemJoin('court-case-invoicing-form')
  const { formatMessage } = useIntl()
  const { data, loading, refetch } = useCourtCaseContext()
  const { pushNotification } = useNotifications()
  const { files, setFiles, addFile, removeFile } = useHandleFileUpload()
  const [upsertCourtCasePaymentEvent] = useUpsertCourtCasePaymentEventMutation({
    onError: (error) => {
      pushNotification({
        type: AlertType.error,
        message: <FormattedError error={error} />
      })
    },
    onCompleted: () => {
      pushNotification({
        type: AlertType.success,
        message: formatMessage({ id: 'CourtCases.Invoicing.SuccessAlert' })
      })
    }
  })

  const methods = useForm({
    defaultValues: {
      ProceduralFee: 0,
      AppointmentFee: 0,
      SettlementFee: 0,
      Expenses: 0,
      OtherCosts: 0,
      Vat: 0
    }
  })

  const handleSubmit = useAsyncCallback(async (values) => {
    const uploadedDocumentIds = await uploadFiles({ files })

    await upsertCourtCasePaymentEvent({
      refetchQueries: [
        refetchCourtCasePaymentEventsQuery({
          courtCaseUuid: data!.uuid,
          filters: { type: { equals: CourtCasePaymentEventType.FirstInstance } },
          sorts: { createdAt: SortDirection.Asc }
        })
      ],
      awaitRefetchQueries: true,
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          courtCaseUuid: data!.uuid,
          payload: values,
          type: values.Instance,
          metadata: {
            version: 1,
            lawyerDocumentIds: uploadedDocumentIds
          }
        }
      }
    })

    setFiles([])
    await refetch()
  })

  const checkKeyDown = (e) => {
    if (e.key === 'Enter') e.preventDefault()
  }

  if (loading || !data) return <Skeleton count={3} />

  return (
    <FormProvider {...methods}>
      <InvoicingDefaultValuesProvider>
        <InvoicingFeeTablesProvider>
          <form
            onSubmit={methods.handleSubmit(handleSubmit.execute)}
            onKeyDown={(e) => checkKeyDown(e)}
            className={b()}
          >
            <InvoicingInstancesSection />
            <InvoicingBasicSection />
            <InvoicingOwnCostSection />
            <InvoicingTotalCostSection />
            <InvoicingFinalizeSection
              files={files}
              onFileUpload={addFile}
              onFileRemove={removeFile}
              isSubmitting={handleSubmit.loading}
            />
          </form>
        </InvoicingFeeTablesProvider>
      </InvoicingDefaultValuesProvider>
    </FormProvider>
  )
}
